import { faTruckClock } from "@fortawesome/pro-solid-svg-icons";
import {
	Button,
	ContactButton,
	NativeInput,
	Typography,
	formatNumber,
	getConstants,
} from "@tosize/ui";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { match } from "ts-pattern";
import { PriceDisplay } from "../../../../../apps/frontend/graphql";
import { useConfig } from "../../context/ConfigContext";
import { useCabinetStore } from "../../context/StoreContext";
import { getMoneyValue, usePrice } from "../../hooks/usePrice";
import { SaveDialog } from "../Save";
import { serialize } from "../renderer/data";

export default function Order() {
	const intl = useIntl();
	const price = usePrice();
	const { onAddToCart, mode, quantity } = useConfig();
	const { state } = useCabinetStore();
	const constants = getConstants();
	const [isLoading, setIsLoading] = useState(false);
	const [amount, setAmount] = useState(quantity ?? 1);
	const orderRef = useRef<HTMLDivElement>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: This effect should only run when the price variant changes
	useEffect(() => {
		const element = orderRef.current;
		const parent = element?.parentElement;

		if (parent) {
			parent.style.setProperty("--order-y", `${element.clientHeight}px`);
		}

		return () => {
			parent?.style.removeProperty("--order-y");
		};
	}, [price.variant]);

	return (
		<div
			ref={orderRef}
			id="order-furniture"
			className="target container space-y-2 border-shades-medium border-t bg-shades-light p-4 xl:absolute xl:right-4 xl:bottom-4 xl:max-w-sm xl:rounded-xl xl:border-t-0 xl:shadow xl:backdrop-blur-3xl 2xl:max-w-md"
		>
			<div className="flex items-center justify-between">
				{match(price)
					.with({ variant: "calculating" }, () => (
						<div>
							<FormattedMessage defaultMessage="Prijs wordt berekend" />
						</div>
					))
					.with({ variant: "error" }, () => (
						<div>
							<span>
								<FormattedMessage defaultMessage="Prijs kan niet worden berekend" />
							</span>
							<div className="text-shades-dark text-sm leading-tight">
								<FormattedMessage defaultMessage="De rood gearceerde onderdelen zijn te groot of te klein. Sleep de aangrenzende staanders en liggers om een nieuwe prijs te berekenen." />
								<br />
								<ContactButton href="/contacts?type=place_order">
									<FormattedMessage defaultMessage="Lukt het niet? Neem contact op met de klantenservice." />
								</ContactButton>
							</div>
						</div>
					))
					.with({ variant: "default" }, (p) => (
						<div className="mt-1 flex items-end gap-2 child:leading-none">
							<span className="text-success-medium text-xl">
								{formatNumber({
									lang: intl.locale,
									value: getMoneyValue(p.money),
									style: "decimal-hyphen",
								})}
							</span>

							<span className="text-shades-dark text-sm">
								{p.money.display !== PriceDisplay.IncludingTax ? (
									<FormattedMessage defaultMessage="excl. BTW" />
								) : (
									<FormattedMessage defaultMessage="incl. BTW" />
								)}
							</span>
						</div>
					))
					.exhaustive()}
			</div>

			<div className="flex flex-col items-center justify-center gap-2">
				<div className="flex w-full gap-2">
					<NativeInput
						type="number"
						inputMode="numeric"
						step="1"
						min="1"
						value={amount}
						onChange={(e) => setAmount(e.target.valueAsNumber)}
						className="max-w-[4rem]"
					/>
					<Button
						color="success"
						className="w-full"
						isLoading={isLoading}
						onClick={async () => {
							if (onAddToCart) {
								setIsLoading(true);

								await onAddToCart(serialize(state), amount);

								setIsLoading(false);
							}
						}}
					>
						{mode === "editCartItem" || mode === "editProjectItem" ? (
							<FormattedMessage defaultMessage="Aanpassingen opslaan" />
						) : (
							<FormattedMessage defaultMessage="Toevoegen aan winkelwagen" />
						)}
					</Button>
				</div>

				<SaveDialog />
			</div>

			<div>
				<Typography
					variant="paragraph"
					starticon={faTruckClock}
					size="text-sm"
					className="flex items-center justify-center leading-tight"
				>
					<FormattedMessage
						defaultMessage="Wij leveren jouw maatwerk vakkenkast binnen {min} tot {max} weken"
						values={{
							min: constants.deliveryTime.furniture.min,
							max: constants.deliveryTime.furniture.max,
						}}
					/>
				</Typography>
			</div>
		</div>
	);
}
